import Link from "next/link"

import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem
} from "@/components/ui/carousel"
import type { LobbyGame, LobbySuggestion } from "@/domain/casino/listing/types"
import { categoryGamesPreviewTexts as t } from "@/features/casino/localization/texts"
import GameCard from "@/stories/molecules/cards/gameCard/GameCard"

export const PREVIEW_COUNT = 10

// Below 440px the three cards fill the padded row (10px each side, 8px gaps).
// From 440px the 360px size is frozen so the fourth card peeks.
// Tablet and desktop use the 187×231 card.
const PREVIEW_SLIDE_CLASS =
  "min-w-0 shrink-0 grow-0 basis-[calc((100%-16px)/3)] min-[440px]:max-md:basis-[calc((min(100vw,360px)-36px)/3)] md:basis-[187px]"

const PREVIEW_SKELETON_KEYS = [
  "preview-a",
  "preview-b",
  "preview-c",
  "preview-d"
] as const

export type PreviewGame = {
  identifier: string
  title: string
  producer: string
  provider: string
  thumb_url?: string
  ai_thumb_url?: string | null
  usd_only?: boolean
  currency: string | null
  sections?: {
    recomendados?: boolean
    populares?: boolean
    nuevos?: boolean
  }
  released_at: string | null
}

export type GamesPreviewRowProps = {
  title: string
  href: string
  games: PreviewGame[]
  categorySelected: string
  icon?: ReactNode
  showHeader?: boolean
  /** How many leading cards preload. Home passes 0 so they do not compete with the hero. */
  priorityCount?: number
}

export type GamesPreviewRowEmptyProps = {
  title: string
  href: string
  icon?: ReactNode
}

function PreviewRowHeader({ title, href, icon }: GamesPreviewRowEmptyProps) {
  return (
    <header className="flex items-center justify-between gap-2">
      <h2 className="flex min-w-0 items-center gap-2 font-gochi text-[20px] font-normal leading-none uppercase">
        {icon}
        <span
          className="truncate leading-none"
          style={{ transform: "translateY(3px)" }}
        >
          {title}
        </span>
      </h2>
      <Button asChild variant="muted">
        <Link href={href} prefetch aria-label={`${t.seeAllAria}: ${title}`}>
          {t.button}
        </Link>
      </Button>
    </header>
  )
}

export function GamesPreviewRow({
  title,
  href,
  games,
  categorySelected,
  icon,
  showHeader = true,
  priorityCount = 3
}: GamesPreviewRowProps) {
  return (
    <section aria-label={title}>
      <div className="flex flex-col gap-3.5">
        {showHeader ? (
          <PreviewRowHeader title={title} href={href} icon={icon} />
        ) : null}
        <Carousel className="w-full" opts={{ align: "start" }}>
          <CarouselContent className="gap-2 xlg:gap-4">
            {games.map((game, index) => (
              <CarouselItem
                key={game.identifier}
                className={PREVIEW_SLIDE_CLASS}
              >
                <GameCard
                  game={game}
                  index={index}
                  priority={index < priorityCount}
                  categorySelected={categorySelected}
                  showFavoriteButton
                  showCurrencyBadge={false}
                  showPlayButton
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  )
}

export function GamesPreviewRowEmpty({
  title,
  href,
  icon
}: GamesPreviewRowEmptyProps) {
  return (
    <section aria-label={title}>
      <div className="flex flex-col gap-3.5">
        <PreviewRowHeader title={title} href={href} icon={icon} />
        <div className="relative">
          <div aria-hidden className="grid grid-cols-3 gap-2 xlg:gap-4">
            <div className="aspect-118/146 md:aspect-187/231" />
          </div>
          <p className="absolute inset-0 flex items-center justify-center rounded-lg bg-default-100/40 px-4 text-center text-xs text-default-500">
            {t.empty}
          </p>
        </div>
      </div>
    </section>
  )
}

export function GamesPreviewRowSkeleton() {
  return (
    <section aria-hidden>
      <div className="flex flex-col gap-3.5">
        <div className="flex items-center justify-between gap-2">
          <div className="h-5 w-24 rounded bg-default-100" />
          <div className="h-11 w-24 rounded bg-default-100" />
        </div>
        <div className="flex gap-2 overflow-hidden xlg:gap-4">
          {PREVIEW_SKELETON_KEYS.map((key) => (
            <div
              key={key}
              className={`${PREVIEW_SLIDE_CLASS} aspect-118/146 rounded-lg bg-default-100 md:aspect-187/231`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export function toPreviewGames(games: LobbyGame[]): PreviewGame[] {
  return games
    .flatMap((game) => {
      if (!game.identifier) {
        return []
      }

      return [
        {
          identifier: game.identifier,
          title: game.title ?? "",
          producer: game.producer ?? "",
          provider: game.provider ?? "",
          thumb_url: game.thumb_url ?? undefined,
          ai_thumb_url: game.ai_thumb_url,
          usd_only: game.usd_only ?? undefined,
          currency: game.currency,
          sections: toGameCardSections(game.sections),
          released_at: toReleasedAt(game.released_at)
        }
      ]
    })
    .slice(0, PREVIEW_COUNT)
}

export function toPreviewSuggestions(games: LobbySuggestion[]): PreviewGame[] {
  return games.flatMap((game) => {
    if (!game.identifier) {
      return []
    }

    return [
      {
        identifier: game.identifier,
        title: game.title ?? "",
        producer: game.producer ?? "",
        provider: game.provider ?? "",
        thumb_url: game.thumb_url ?? undefined,
        ai_thumb_url: game.ai_thumb_url,
        currency: game.currency,
        released_at: null
      }
    ]
  })
}

function toGameCardSections(sections: unknown): PreviewGame["sections"] {
  if (!sections || typeof sections !== "object" || Array.isArray(sections)) {
    return undefined
  }

  const record = sections as Record<string, unknown>

  return {
    recomendados: Boolean(record.recomendados),
    populares: Boolean(record.populares),
    nuevos: Boolean(record.nuevos)
  }
}

function toReleasedAt(value: LobbyGame["released_at"]): string | null {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  return value
}
